/**
 * Observatory Signal — the vocabulary of the signal-triage pipeline.
 *
 * Every string field of the Convex observatory store (the `obs*` tables in
 * `apps/ops-store/convex/tables.obs.ts`, KTD7) is governed by one of the closed unions
 * below, never a database enum (KD8): a new value is a change here, not a
 * schema migration. The pure core (`tooling/observatory/signals`), the backend
 * collectors and writers, the `/internal/observatory/signals` read surface and
 * both CLIs parse against these, so a value that is not listed is refused at
 * every boundary rather than stored and half-understood.
 *
 * Plan: docs/plans/2026-09-26-1745-feat-observatory-signal-triage-pipeline-plan.md (U1, U15, KTD7)
 */

import { z } from 'zod'

// =============================================================================
// Vocabularies
// =============================================================================

/**
 * Where a signal came from (R1, R4). `observer` is the sync observer's findings,
 * absence findings included, ingested as their own source so its detectors keep
 * running unchanged.
 */
export const OBSERVATORY_SIGNAL_SOURCES = ['jobs', 'observer', 'datadog', 'planetscale', 'posthog'] as const
export const ObservatorySignalSourceSchema = z.enum(OBSERVATORY_SIGNAL_SOURCES)
export type ObservatorySignalSource = z.infer<typeof ObservatorySignalSourceSchema>

/**
 * A collector's verdict on its own read for one tick (R1).
 *
 * - `ok` — the read completed.
 * - `partial` — a row cap or limit was hit, or a completeness check failed.
 * - `failed` — the read errored.
 * - `skipped` — a credential or scope is missing.
 *
 * Anything but `ok` holds the state of that source's signals: a failed or
 * partial read never clears a signal (R3).
 */
export const OBSERVATORY_SOURCE_HEALTH = ['ok', 'partial', 'failed', 'skipped'] as const
export const ObservatorySourceHealthSchema = z.enum(OBSERVATORY_SOURCE_HEALTH)
export type ObservatorySourceHealth = z.infer<typeof ObservatorySourceHealthSchema>

/**
 * Lifecycle state of a signal or an Issue (KTD5). `clearing` is the first clear
 * observation; a second consecutive one resolves. `suppressed` is a state, set
 * by a suppression rule or a human cancel, and never a severity (KTD4 rule 1).
 * `regressed` is a transition (resolved → open, a new episode), not a state.
 */
export const OBSERVATORY_SIGNAL_STATES = ['open', 'clearing', 'resolved', 'suppressed'] as const
export const ObservatorySignalStateSchema = z.enum(OBSERVATORY_SIGNAL_STATES)
export type ObservatorySignalState = z.infer<typeof ObservatorySignalStateSchema>

/** Policy severity, lowest first (KTD4). The order is load-bearing: rules add levels by index. */
export const OBSERVATORY_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const
export const ObservatorySeveritySchema = z.enum(OBSERVATORY_SEVERITIES)
export type ObservatorySeverity = z.infer<typeof ObservatorySeveritySchema>

/**
 * Jev's cause answer (KTD8): the spike's six causes, plus `client-environment`
 * for user-session sources, plus `unknown` for "not yet classified" and for a
 * Jev failure. The policy treats `unknown` as `real-defect` for level purposes,
 * so a Jev outage can only surface more, never hide.
 */
export const OBSERVATORY_CAUSES = [
  'real-defect',
  'upstream-outage',
  'capacity',
  'config-drift',
  'empty-or-expected',
  'log-noise',
  'client-environment',
  'unknown'
] as const
export const ObservatoryCauseSchema = z.enum(OBSERVATORY_CAUSES)
export type ObservatoryCause = z.infer<typeof ObservatoryCauseSchema>

/** Inferred firing rhythm, which sets the clearing window (KTD5). */
export const OBSERVATORY_CADENCES = ['continuous', 'hourly', 'daily'] as const
export const ObservatoryCadenceSchema = z.enum(OBSERVATORY_CADENCES)
export type ObservatoryCadence = z.infer<typeof ObservatoryCadenceSchema>

/**
 * The claimable actions on an Issue episode (KTD3, U10, U11). One claim row per
 * (Issue, episode, action, seq). `alert` is the once-per-episode critical Slack
 * alert (KTD16). `none` is a reconcile outcome, not a claim, so it is not a
 * member.
 */
export const OBSERVATORY_ACTION_KINDS = [
  'investigate',
  'file',
  'comment',
  'comment-canceled',
  'refile-episode',
  'suppress-implicit',
  'alert'
] as const
export const ObservatoryActionKindSchema = z.enum(OBSERVATORY_ACTION_KINDS)
export type ObservatoryActionKind = z.infer<typeof ObservatoryActionKindSchema>

/** A claim's lease status (KTD3). A `claimed` row past its lease is reclaimable. */
export const OBSERVATORY_ACTION_STATUSES = ['claimed', 'done', 'failed'] as const
export const ObservatoryActionStatusSchema = z.enum(OBSERVATORY_ACTION_STATUSES)
export type ObservatoryActionStatus = z.infer<typeof ObservatoryActionStatusSchema>

/**
 * An investigation's outcome (U10). `confirmed` passed the post-check;
 * `unconfirmed`, `failed` and `capped` all file evidence-only (KTD11).
 */
export const OBSERVATORY_INVESTIGATION_STATUSES = ['running', 'confirmed', 'unconfirmed', 'failed', 'capped'] as const
export const ObservatoryInvestigationStatusSchema = z.enum(OBSERVATORY_INVESTIGATION_STATUSES)
export type ObservatoryInvestigationStatus = z.infer<typeof ObservatoryInvestigationStatusSchema>

/**
 * Who wrote a label, or closed a ticket. A label arriving on the agent key is
 * stored as `agent` whatever its body claims (U12), and an agent label is inert
 * until a human acknowledges it (R16).
 */
export const OBSERVATORY_ACTOR_TYPES = ['human', 'agent'] as const
export const ObservatoryActorTypeSchema = z.enum(OBSERVATORY_ACTOR_TYPES)
export type ObservatoryActorType = z.infer<typeof ObservatoryActorTypeSchema>

/** Who closed a linked ticket, from `loop/human-touch.ts` (R14). */
export const ObservatoryTicketClosedBySchema = ObservatoryActorTypeSchema
export type ObservatoryTicketClosedBy = ObservatoryActorType

/** Linear's workflow state types, as read for a linked ticket. */
export const OBSERVATORY_TICKET_STATES = ['triage', 'backlog', 'unstarted', 'started', 'completed', 'canceled'] as const
export const ObservatoryTicketStateSchema = z.enum(OBSERVATORY_TICKET_STATES)
export type ObservatoryTicketState = z.infer<typeof ObservatoryTicketStateSchema>

/**
 * How a signal belongs to its Issue (KTD6, U2b). `definitive` is a log-index
 * location plus error class join (or the Issue's anchor, or a school-level
 * constituent) and is sticky. `provisional` is a join on table or sync type, or
 * on message tokens, alone: re-evaluated every tick, it adds no corroboration
 * and stays out of investigation bundles, ticket bodies and comments until three
 * consecutive corroborating ticks promote it.
 */
export const OBSERVATORY_SIGNAL_MEMBERSHIPS = ['definitive', 'provisional'] as const
export const ObservatorySignalMembershipSchema = z.enum(OBSERVATORY_SIGNAL_MEMBERSHIPS)
export type ObservatorySignalMembership = z.infer<typeof ObservatorySignalMembershipSchema>

/**
 * How a collect tick ended (KTD15). `abandoned` is set by the next tick on a
 * run left unfinished for more than 30 minutes, so one crash cannot stall
 * collection.
 */
export const OBSERVATORY_RUN_OUTCOMES = ['completed', 'failed', 'abandoned'] as const
export const ObservatoryRunOutcomeSchema = z.enum(OBSERVATORY_RUN_OUTCOMES)
export type ObservatoryRunOutcome = z.infer<typeof ObservatoryRunOutcomeSchema>

/**
 * How a Jev call ended (R8, U4). Only `classified` carries a cause; every other
 * status leaves it `unknown` (KTD8): `low-confidence` answered below the floor,
 * `invalid-answer` named no allowed cause, `timeout` and `error` got no answer.
 */
export const OBSERVATORY_CLASSIFICATION_STATUSES = [
  'classified',
  'low-confidence',
  'invalid-answer',
  'timeout',
  'error'
] as const
export const ObservatoryClassificationStatusSchema = z.enum(OBSERVATORY_CLASSIFICATION_STATUSES)
export type ObservatoryClassificationStatus = z.infer<typeof ObservatoryClassificationStatusSchema>

/**
 * An evaluation case's verdict (R20): was an externally reported problem seen
 * before the report, only after it, never, or is it outside what any source
 * can see. `not-observable` cases are excluded from recall (U16).
 */
export const OBSERVATORY_EVAL_VERDICTS = ['found-before', 'found-after', 'missed', 'not-observable'] as const
export const ObservatoryEvalVerdictSchema = z.enum(OBSERVATORY_EVAL_VERDICTS)
export type ObservatoryEvalVerdict = z.infer<typeof ObservatoryEvalVerdictSchema>

/** Why a problem was missed or found late (R20): a closed list, so misses can be counted by reason. */
export const OBSERVATORY_EVAL_MISS_REASONS = [
  'no-source',
  'below-threshold',
  'suppressed',
  'mis-correlated',
  'mis-graded',
  'capped',
  'collector-failed',
  'other'
] as const
export const ObservatoryEvalMissReasonSchema = z.enum(OBSERVATORY_EVAL_MISS_REASONS)
export type ObservatoryEvalMissReason = z.infer<typeof ObservatoryEvalMissReasonSchema>

/** The filing switch (R18). */
export const OBSERVATORY_SIGNAL_TRIAGE_MODES = ['shadow', 'live'] as const
export const ObservatorySignalTriageModeSchema = z.enum(OBSERVATORY_SIGNAL_TRIAGE_MODES)
export type ObservatorySignalTriageMode = z.infer<typeof ObservatorySignalTriageModeSchema>

/** What a label targets. */
export const OBSERVATORY_LABEL_TARGETS = ['signal', 'issue'] as const
export const ObservatoryLabelTargetSchema = z.enum(OBSERVATORY_LABEL_TARGETS)
export type ObservatoryLabelTarget = z.infer<typeof ObservatoryLabelTargetSchema>

/**
 * Which field a label overrides. `issue` moves a signal to another Issue (the
 * only way membership moves, KTD6); its value is the target `issueKey`.
 */
export const OBSERVATORY_LABEL_FIELDS = ['severity', 'cause', 'state', 'issue'] as const
export const ObservatoryLabelFieldSchema = z.enum(OBSERVATORY_LABEL_FIELDS)
export type ObservatoryLabelField = z.infer<typeof ObservatoryLabelFieldSchema>

/** Where a label write came from. `filing-reconcile` is the implicit suppression on a human cancel (R14). */
export const OBSERVATORY_LABEL_SURFACES = ['obs-cli', 'internal-api', 'filing-reconcile'] as const
export const ObservatoryLabelSurfaceSchema = z.enum(OBSERVATORY_LABEL_SURFACES)
export type ObservatoryLabelSurface = z.infer<typeof ObservatoryLabelSurfaceSchema>

// =============================================================================
// Shapes
// =============================================================================

/** One policy rule that fired, in evaluation order (R9). */
export const ObservatoryPolicyTraceEntrySchema = z.object({
  /** Stable rule id, e.g. `base-level`, `new-at-volume`, `ceiling-noise`, or a suppression rule's key. */
  ruleId: z.string().min(1).max(64),
  /** Human-readable reason, e.g. `+1: 2 sources corroborate`. */
  detail: z.string().max(500)
})
export type ObservatoryPolicyTraceEntry = z.infer<typeof ObservatoryPolicyTraceEntrySchema>

export const ObservatoryPolicyTraceSchema = z.array(ObservatoryPolicyTraceEntrySchema)
export type ObservatoryPolicyTrace = z.infer<typeof ObservatoryPolicyTraceSchema>

/**
 * A signal's deterministic facts (R2). Collectors may add source-specific keys,
 * so unknown keys pass through; the listed ones are what the policy reads.
 * `schoolIds` is why the tables carry no school column (KTD7).
 */
export const ObservatorySignalFactsSchema = z.looseObject({
  count: z.number().int().nonnegative(),
  previousCount: z.number().int().nonnegative().nullable(),
  level: z.string().min(1).max(16).nullable(),
  schoolIds: z.array(z.number().int().positive()),
  payingSchoolIds: z.array(z.number().int().positive()),
  distinctUsers: z.number().int().nonnegative().nullable(),
  firstSeenAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime()
})
export type ObservatorySignalFacts = z.infer<typeof ObservatorySignalFactsSchema>

/** A code location from the build-time log index (R6). Lines live here only, never in an Issue key (KTD6). */
export const ObservatoryCodeRefSchema = z.object({
  file: z.string().min(1),
  line: z.number().int().positive(),
  operation: z.string().min(1).nullable()
})
export type ObservatoryCodeRef = z.infer<typeof ObservatoryCodeRefSchema>

/**
 * A signal as the read surface returns it (R16). `id` is the public id, which
 * is the fingerprint: the store links by natural keys, never its own document
 * ids (KTD7). `severity` is null exactly when the signal is suppressed.
 */
export const ObservatorySignalSchema = z
  .object({
    id: z.string().min(1),
    fingerprint: z.string().min(1).max(191),
    source: ObservatorySignalSourceSchema,
    kind: z.string().min(1).max(64),
    title: z.string().min(1).max(500),
    cadence: ObservatoryCadenceSchema.nullable(),
    state: ObservatorySignalStateSchema,
    clearStreak: z.number().int().nonnegative(),
    severity: ObservatorySeveritySchema.nullable(),
    /** Null before the policy has run on this signal. */
    policyVersion: z.string().min(1).max(32).nullable(),
    policyTrace: ObservatoryPolicyTraceSchema,
    cause: ObservatoryCauseSchema,
    causeConfidence: z.number().min(0).max(1).nullable(),
    jevModel: z.string().min(1).max(64).nullable(),
    facts: ObservatorySignalFactsSchema,
    /** The Issue's `issueKey`, or null before correlation. */
    issueKey: z.string().min(1).max(191).nullable(),
    /** How the signal belongs to that Issue (U2b), or null before correlation. */
    membership: ObservatorySignalMembershipSchema.nullable(),
    firstSeenAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime()
  })
  .refine(s => (s.state === 'suppressed') === (s.severity === null), {
    message: 'severity is null exactly when state is suppressed',
    path: ['severity']
  })
export type ObservatorySignal = z.infer<typeof ObservatorySignalSchema>

/**
 * An Issue as the read surface returns it. `id` is the `issueKey` (KTD7).
 * `episodeMaxSeverity` is the KTD4 latch that filing reads.
 */
export const ObservatoryIssueSchema = z.object({
  id: z.string().min(1),
  issueKey: z.string().min(1).max(191),
  title: z.string().min(1).max(500),
  state: ObservatorySignalStateSchema,
  severity: ObservatorySeveritySchema.nullable(),
  episodeMaxSeverity: ObservatorySeveritySchema.nullable(),
  episode: z.number().int().min(1),
  episodeStartedAt: z.iso.datetime(),
  codeRefs: z.array(ObservatoryCodeRefSchema),
  ticketRef: z.string().min(1).max(64).nullable(),
  ticketState: ObservatoryTicketStateSchema.nullable(),
  ticketClosedBy: ObservatoryTicketClosedBySchema.nullable(),
  ticketCompletedAt: z.iso.datetime().nullable(),
  lastCommentAt: z.iso.datetime().nullable(),
  /**
   * Correlation state the collect tick carries between ticks (U9, KTD6): the
   * anchor's error class, which decides who may join, and the provisional
   * members with their runs of corroborating ticks (U2b). Optional: an Issue
   * written before U9 has neither.
   */
  errorClass: z.string().min(1).max(64).nullable().optional(),
  provisional: z
    .array(z.object({ fingerprint: z.string().min(1).max(191), streak: z.number().int().nonnegative() }))
    .optional(),
  /** Ticket refs of earlier episodes (KTD6): kept so a replace never drops them. */
  priorTickets: z.array(z.object({ episode: z.number().int().min(1), ticketRef: z.string().min(1).max(64) })).optional()
})
export type ObservatoryIssue = z.infer<typeof ObservatoryIssueSchema>

/**
 * One collect tick (R1). `id` is the tick's `runId`. `sourceHealth` names every
 * source, `skipped` included, once the run finishes. `deployObserved` is set by
 * the store when the image sha differs from the previous run's (KTD12).
 */
export const ObservatorySignalRunSchema = z.object({
  id: z.string().min(1),
  mode: ObservatorySignalTriageModeSchema,
  imageSha: z.string().min(1).max(64).nullable(),
  deployObserved: z.boolean(),
  policyVersion: z.string().min(1).max(32),
  sourceHealth: z.partialRecord(ObservatorySignalSourceSchema, ObservatorySourceHealthSchema),
  counts: z.record(z.string(), z.number().int().nonnegative()).nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  /** Null while the run is unfinished. */
  outcome: ObservatoryRunOutcomeSchema.nullable()
})
export type ObservatorySignalRun = z.infer<typeof ObservatorySignalRunSchema>

/**
 * Who acted, for every human-authority write (labels, acknowledgements, rules,
 * the config flip). Refuses the `operator` literal, which names nobody.
 */
export const ObservatoryActorIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(191)
  .refine(v => v.toLowerCase() !== 'operator', { message: "actorId must name who acted, not 'operator'" })

/**
 * A label as written (R16). `actorType` and `actorId` are both required: an
 * override without provenance cannot be weighed (human beats agent) or
 * exported.
 */
export const ObservatorySignalLabelInputSchema = z.object({
  targetType: ObservatoryLabelTargetSchema,
  /** The signal's fingerprint or the Issue's issueKey (the store's natural keys, KTD7). */
  targetId: z.string().min(1),
  field: ObservatoryLabelFieldSchema,
  value: z.string().min(1).max(191),
  actorType: ObservatoryActorTypeSchema,
  actorId: ObservatoryActorIdSchema,
  surface: ObservatoryLabelSurfaceSchema,
  reason: z.string().max(1000).nullable().optional()
})
export type ObservatorySignalLabelInput = z.infer<typeof ObservatorySignalLabelInputSchema>

/** A stored label, with what it overrode and its acknowledgement. */
export const ObservatorySignalLabelSchema = ObservatorySignalLabelInputSchema.extend({
  id: z.string().min(1),
  reason: z.string().max(1000).nullable(),
  policyVersion: z.string().min(1).max(32).nullable(),
  jevModel: z.string().min(1).max(64).nullable(),
  acknowledgedAt: z.iso.datetime().nullable(),
  acknowledgedBy: z.string().min(1).max(191).nullable(),
  createdAt: z.iso.datetime()
})
export type ObservatorySignalLabel = z.infer<typeof ObservatorySignalLabelSchema>

/**
 * A stored known-noise rule (R10). `id` is the `ruleKey`. `match` is
 * interpreted by the pure core's matcher (U2), so it stays an open record here.
 * `expiresAt` null never expires.
 */
export const ObservatorySuppressionRuleSchema = z.object({
  id: z.string().min(1),
  ruleKey: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/, 'lowercase kebab-case, at most 64 characters'),
  match: z.record(z.string(), z.unknown()),
  owner: z.string().min(1).max(64),
  reason: z.string().min(1).max(1000),
  createdBy: z.string().min(1).max(191),
  expiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime()
})
export type ObservatorySuppressionRule = z.infer<typeof ObservatorySuppressionRuleSchema>

/**
 * One Jev call, append-only (R8, KTD7 `obsClassifications`). `jevModel` is the
 * resolved model version, never the `~typesafe/jev-latest` alias, and
 * `rawAnswer` is scrubbed before it is stored. `id` is
 * `<fingerprint>@<classifiedAt>`: one call per signal per tick.
 */
export const ObservatorySignalClassificationSchema = z.object({
  id: z.string().min(1),
  fingerprint: z.string().min(1).max(191),
  /** Hash of the material facts the call saw; an unchanged hash is never re-sent (KTD8). */
  factsHash: z.string().min(1).max(128),
  classifiedAt: z.iso.datetime(),
  status: ObservatoryClassificationStatusSchema,
  cause: ObservatoryCauseSchema,
  confidence: z.number().min(0).max(1).nullable(),
  jevModel: z.string().min(1).max(64).nullable(),
  rawAnswer: z.string().nullable(),
  error: z.string().nullable(),
  /**
   * Consecutive `unknown` answers for this fingerprint and facts hash, this one
   * included; 0 once a cause resolves. The `unknown` retry schedule reads it
   * (KTD8: the next tick, then every 6 hours).
   */
  unknownStreak: z.number().int().nonnegative()
})
export type ObservatorySignalClassification = z.infer<typeof ObservatorySignalClassificationSchema>

/**
 * A leased claim on one (Issue, episode, action, seq) (KTD3, `obsActions`).
 * A `claimed` claim past `leaseExpiresAt`, or a `failed` one, can be taken
 * over; a `done` one never. `critical` filings bypass the R19 caps and are not
 * counted against them.
 */
export const ObservatoryActionClaimSchema = z.object({
  issueKey: z.string().min(1).max(191),
  episode: z.number().int().min(1),
  action: ObservatoryActionKindSchema,
  seq: z.number().int().nonnegative(),
  status: ObservatoryActionStatusSchema,
  holder: z.string().min(1),
  runId: z.string().min(1).nullable(),
  critical: z.boolean(),
  claimedAt: z.iso.datetime(),
  leaseExpiresAt: z.iso.datetime(),
  attempts: z.number().int().min(1),
  completedAt: z.iso.datetime().nullable(),
  ticketRef: z.string().min(1).max(64).nullable(),
  error: z.string().nullable()
})
export type ObservatoryActionClaim = z.infer<typeof ObservatoryActionClaimSchema>

/**
 * An externally reported problem checked against the observatory (R20, KTD18,
 * `obsEvalCases`). `id` is `externalRef`: a SCH key, Slack permalink or
 * incident id. A human write stands acknowledged; an agent-proposed case stays
 * out of the scorecard until a human acknowledges it. `leadMinutes` is the
 * report time minus the Issue's first-seen, negative when found after.
 */
export const ObservatoryEvalCaseSchema = z.object({
  id: z.string().min(1),
  externalRef: z.string().min(1).max(500),
  reportedAt: z.iso.datetime(),
  summary: z.string().min(1).max(2000),
  schoolIds: z.array(z.number().int().positive()),
  issueKey: z.string().min(1).max(191).nullable(),
  verdict: ObservatoryEvalVerdictSchema,
  leadMinutes: z.number().int().nullable(),
  severityAtReport: ObservatorySeveritySchema.nullable(),
  ticketStateAtReport: ObservatoryTicketStateSchema.nullable(),
  /** Set for `missed` and `found-after`. */
  missReason: ObservatoryEvalMissReasonSchema.nullable(),
  gapTicketRef: z.string().min(1).max(64).nullable(),
  actorType: ObservatoryActorTypeSchema,
  actorId: z.string().min(1).max(191),
  acknowledgedAt: z.iso.datetime().nullable(),
  acknowledgedBy: z.string().min(1).max(191).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
})
export type ObservatoryEvalCase = z.infer<typeof ObservatoryEvalCaseSchema>

// =============================================================================
// Config document (KTD13, R18, R19)
// =============================================================================

/**
 * Live-filing caps (R19). Critical Issues are never capped, which is a rule of
 * the filer, not a number here: there is deliberately no `critical` field to
 * set. Caps are checked before a claim is inserted, so a deferred filing holds
 * no claim (KTD3).
 */
export const ObservatorySignalTriageCapsSchema = z.object({
  /** New non-critical tickets per collect tick. */
  newTicketsPerTick: z.number().int().min(0).default(5),
  /** New tickets per Issue per hour. */
  perIssuePerHour: z.number().int().min(0).default(1),
  /** New non-critical tickets per day. */
  perDay: z.number().int().min(0).default(10)
})
export type ObservatorySignalTriageCaps = z.infer<typeof ObservatorySignalTriageCapsSchema>

/**
 * The `obsConfig` document in the Convex observatory store (one document,
 * KTD13). An absent document, or a failed read, parses to shadow with the R19
 * caps, so the unsafe direction (filing) always needs a human to write `live`
 * and a successful read to see it. Even then, filing also requires an ECS
 * execution context (KTD13), a guard that lives in the writer and does not
 * depend on this document.
 */
export const ObservatorySignalTriageConfigSchema = z.object({
  mode: ObservatorySignalTriageModeSchema.default('shadow'),
  // `.prefault`, not `.default`: Zod 4 returns a `.default` value unparsed, so
  // the inner cap defaults would never apply (see `factory-config.ts`).
  caps: ObservatorySignalTriageCapsSchema.prefault({})
})
export type ObservatorySignalTriageConfig = z.infer<typeof ObservatorySignalTriageConfigSchema>

export const OBSERVATORY_SIGNAL_TRIAGE_CONFIG_DEFAULT: ObservatorySignalTriageConfig =
  ObservatorySignalTriageConfigSchema.parse({})
